import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  Kanban,
  KanbanCardBody,
  KanbanTag,
  Page,
  PageHeader,
  Table,
  Toolbar,
  type FilterValues,
  type KanbanColor,
  type KanbanStage,
} from '@evogard/ui';
import type { FinanceExpense, FinanceExpensesQuery } from '@evogard/contract';
import {
  EXPENSE_REQUEST_STAGES,
  EXPENSE_REQUEST_STAGE_LABELS,
  EXPENSE_SOURCE_LABELS,
  EXPENSE_STAGE_LABELS,
  EXPENSE_STAGE_TONES,
  FINANCE_BOARD_SOURCES,
  FINANCE_BOARD_STAGES,
  formatDate,
  formatDueDate,
  loadFailureMessage,
  type ExpenseRequestStage,
  type FinanceBoardStage,
} from '@evogard/domain';
import {
  ExpenseNumber,
  beneficiaryOf,
  expenseAmount,
} from '../../components/expense/SupervisorExpenseBoard.js';
import { LabelTags } from '../../components/labels/LabelsPicker.js';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { can, useSessionContext } from '../../lib/session.js';
import { FinanceExpenseDesk, originOf } from './expenses/FinanceExpenseDesk.js';
import { FINANCE_EXPENSES_KEY as KEY } from './expenses/useFinanceDesk.js';

/** A cor de cada coluna — a paleta de fase, pelo tom da fase. */
// @valor
const STAGE_COLORS: Readonly<Record<FinanceBoardStage, KanbanColor>> = {
  aprovacao_financeiro: 'azul',
  pendente: 'amarelo',
  aguardando_pagamento: 'ciano',
  pagamento_atrasado: 'vermelho',
  pagamento_efetuado: 'verde',
  pagamento_recusado: 'cinza',
};

const STAGES: KanbanStage[] = FINANCE_BOARD_STAGES.map((s) => ({
  id: s,
  name: EXPENSE_STAGE_LABELS[s],
  color: STAGE_COLORS[s],
}));

function CardBody({ expense }: { expense: FinanceExpense }) {
  return (
    <KanbanCardBody
      tags={
        <>
          <LabelTags labels={expense.labels} />
          <KanbanTag>{originOf(expense)}</KanbanTag>
          {expense.alreadyPaid && <KanbanTag tone="sucesso">Já pago</KanbanTag>}
          {!expense.hasTaxInvoice && <KanbanTag tone="alerta">NF pendente</KanbanTag>}
        </>
      }
      title={
        <span className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <ExpenseNumber id={expense.id} />
            <span className="min-w-0 truncate">{beneficiaryOf(expense)}</span>
          </span>
          <span className="shrink-0 tabular-nums">{expenseAmount(expense.amount)}</span>
        </span>
      }
      detail={
        <span className="tabular-nums">
          {expense.dueDate ? `vence ${formatDate(expense.dueDate)}` : 'sem vencimento'}
        </span>
      }
      detailIcon="calendario"
      assignee={expense.assignee?.name ?? null}
      alerts={
        expense.status === 'pendente' && expense.pendingReason ? (
          <KanbanTag tone="alerta" title={`Pendência: ${expense.pendingReason}`}>
            Pendência: {expense.pendingReason}
          </KanbanTag>
        ) : undefined
      }
    />
  );
}

/**
 * O quadro de despesas do Financeiro (`financeiro-03`, 26/10/2026): o
 * sinistro, depois que o supervisor do departamento aprovou, e a assistência —
 * que desde 30/10/2026 entra sem supervisor: o lote mensal em Aprovação
 * Financeiro, o à vista já pago em Aguardando Pagamento.
 * Seis colunas, de Aprovação Financeiro a Pagamento Recusado — o que está com
 * o supervisor não aparece. A premiação continua em Pagamentos Recorrência.
 *
 * O cartão abre a mesa da despesa (`FinanceExpenseDesk`, 27/10/2026), com a
 * anatomia do card do sinistro: responsável, vencimento, etiquetas,
 * Formulário, Atividade, Anexos e o movimento. O cartão do quadro mostra as
 * etiquetas e o responsável, como o do sinistro.
 */
export function ExpensesBoardScreen() {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const [view, setView] = useState<'quadro' | 'tabela'>('quadro');
  const [searchText, setSearchText] = useState('');
  const search = useDebouncedTerm(searchText.trim());
  const [filters, setFilters] = useState<FilterValues>({});
  const [openId, setOpenId] = useState<number | null>(null);

  const text = (k: string) =>
    typeof filters[k] === 'string' && filters[k] ? filters[k] : undefined;
  const query: FinanceExpensesQuery = {
    ...(search ? { search } : {}),
    ...(text('origem')
      ? { sourceType: text('origem') as FinanceExpensesQuery['sourceType'] }
      : {}),
    ...(text('departamento')
      ? { stage: text('departamento') as ExpenseRequestStage }
      : {}),
    ...(text('venceDe') ? { dueFrom: text('venceDe') } : {}),
    ...(text('venceAte') ? { dueTo: text('venceAte') } : {}),
  };

  const list = useQuery({
    queryKey: [...KEY, context?.currentTenant.id, query],
    queryFn: async () =>
      ok(
        await api.financeExpenses.listFinanceExpenses({ query }),
        200,
        loadFailureMessage(undefined, 'as despesas'),
      ),
  });

  const canDecide = can(context, 'finance.expenses', 'decidir');
  /*
   * "Atualizar do Conta Azul" (26/10/2026): por enquanto manual — o micro
   * serviço do Paulo faz a mesma conferência duas vezes por dia, e a
   * plataforma ainda não tem agendamento para isso.
   */
  const sync = useMutation({
    mutationFn: async () =>
      ok(
        await api.financeExpenses.syncContaAzul({ body: {} }),
        200,
        'Não foi possível conferir no Conta Azul.',
      ),
    onSettled: () => client.invalidateQueries({ queryKey: KEY }),
  });

  return (
    <Page measure="cheia">
      <PageHeader title="Despesas">
        As despesas do sinistro que o supervisor aprovou e as da Assistência 24h, até o
        pagamento. O à vista da assistência chega já pago, em Aguardando Pagamento, e é
        baixado na conciliação da Stone. As premiações continuam em Pagamentos Recorrência.
      </PageHeader>
      <Toolbar
        search={{
          label: 'Pesquisar despesa',
          placeholder: 'Beneficiário, descrição ou observação',
          value: searchText,
          onChange: setSearchText,
        }}
        toolbar={
          <>
            <Filters
              values={filters}
              onApply={setFilters}
              fields={[
                {
                  type: 'selecao',
                  name: 'origem',
                  label: 'Origem',
                  placeholder: 'Todas',
                  options: FINANCE_BOARD_SOURCES.map((s) => ({
                    value: s,
                    label: EXPENSE_SOURCE_LABELS[s],
                  })),
                },
                {
                  type: 'selecao',
                  name: 'departamento',
                  label: 'Departamento do sinistro',
                  placeholder: 'Todos',
                  options: EXPENSE_REQUEST_STAGES.map((s) => ({
                    value: s,
                    label: EXPENSE_REQUEST_STAGE_LABELS[s],
                  })),
                },
                {
                  type: 'intervalo',
                  label: 'Vencimento',
                  from: 'venceDe',
                  to: 'venceAte',
                  kind: 'data',
                },
              ]}
            />
            {canDecide && (
              <Button
                variant="contorno"
                icon="atualizar"
                loading={sync.isPending}
                onClick={() => sync.mutate()}
              >
                Atualizar do Conta Azul
              </Button>
            )}
            <Button
              variant="contorno"
              icon={view === 'quadro' ? 'documento' : 'quadro'}
              onClick={() => setView(view === 'quadro' ? 'tabela' : 'quadro')}
            >
              {view === 'quadro' ? 'Ver tabela' : 'Ver quadro'}
            </Button>
          </>
        }
      />
      {sync.isError && <Alert tone="erro">{sync.error.message}</Alert>}
      {sync.data && (
        <Alert
          tone={sync.data.notes.length > 0 ? 'alerta' : 'sucesso'}
          title={
            sync.data.examined === 0
              ? 'Nenhuma despesa lançada esperando pagamento'
              : `${sync.data.examined} conferida${sync.data.examined === 1 ? '' : 's'} no Conta Azul`
          }
        >
          {sync.data.launched > 0 && (
            <p>
              {sync.data.launched} despesa{sync.data.launched === 1 ? '' : 's'} autorizada
              {sync.data.launched === 1 ? '' : 's'} lançada
              {sync.data.launched === 1 ? '' : 's'} agora no Conta Azul.
            </p>
          )}
          {sync.data.examined > 0 && (
            <p>
              {sync.data.paid} paga{sync.data.paid === 1 ? '' : 's'}, {sync.data.late}{' '}
              atrasada{sync.data.late === 1 ? '' : 's'}, {sync.data.unchanged} sem
              mudança.
            </p>
          )}
          {sync.data.notes.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {sync.data.notes.map((n) => (
                <li key={n.expenseId}>
                  Despesa #{n.expenseId}: {n.message}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      {list.isError ? (
        <Alert tone="erro">{list.error.message}</Alert>
      ) : !list.data ? (
        <p className="text-sm text-texto-suave">Carregando as despesas…</p>
      ) : view === 'quadro' ? (
        <Kanban<FinanceExpense>
          label="Quadro de despesas do Financeiro"
          stages={STAGES}
          cardId={(e) => String(e.id)}
          cardLabel={(e) => `${beneficiaryOf(e)}, ${expenseAmount(e.amount)}`}
          loadPage={async (stageId) => {
            const items = (list.data ?? []).filter((e) => e.status === stageId);
            return { items, next: null, total: items.length };
          }}
          reloadKey={list.dataUpdatedAt}
          renderCard={(e) => <CardBody expense={e} />}
          onOpenCard={(e) => setOpenId(e.id)}
          emptyColumnText="Nenhuma despesa nesta fase."
        />
      ) : (
        <Card bleed>
          <Table
            label="Despesas do Financeiro"
            items={list.data}
            rowKey={(e) => String(e.id)}
            rowActions={(e) => [
              { label: 'Abrir', icon: 'olho', run: () => setOpenId(e.id) },
            ]}
            columns={[
              {
                title: 'Nº',
                minWidth: '5rem',
                alignment: 'inicio',
                cell: (e) => <ExpenseNumber id={e.id} />,
              },
              {
                title: 'Beneficiário',
                minWidth: '14rem',
                alignment: 'inicio',
                cell: (e) => beneficiaryOf(e),
              },
              { title: 'Origem', minWidth: '10rem', cell: (e) => originOf(e) },
              {
                title: 'Fase',
                minWidth: '10rem',
                cell: (e) => (
                  <Badge tone={EXPENSE_STAGE_TONES[e.status]}>
                    {EXPENSE_STAGE_LABELS[e.status]}
                  </Badge>
                ),
              },
              {
                title: 'Vencimento',
                minWidth: '7rem',
                cell: (e) => (
                  <span className="tabular-nums">{formatDueDate(e.dueDate)}</span>
                ),
              },
              {
                title: 'Valor',
                minWidth: '8rem',
                alignment: 'fim',
                cell: (e) => (
                  <span className="tabular-nums">{expenseAmount(e.amount)}</span>
                ),
              },
            ]}
            empty="Nenhuma despesa com esses filtros."
          />
        </Card>
      )}
      {openId !== null && (
        <FinanceExpenseDesk id={openId} onClose={() => setOpenId(null)} />
      )}
    </Page>
  );
}
